import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";
import { Header } from "@/components/Header";
import { JwPlayer } from "@/components/JwPlayer";
import { VideoCommentsSection } from "@/components/video-comments/VideoCommentsSection";
import { Button } from "@/components/ui/button";
import { useVideoAccess } from "@/lib/video-access";
import {
  saveWatchTime,
  useUserActivity,
  useVideoBySlug,
  useVideoPlayer,
  type FirebaseVideo,
  type FirestoreVideoMetaPlayer,
} from "@/lib/firebase";
import { debugLog } from "@/lib/debug-log";
import { markActivity } from "@/lib/mark-activity";
import { useAuth } from "@/lib/auth";
import { useDevState } from "@/lib/dev-state";
import { isLibraryOnlyMember } from "@/lib/membership-tier";
import { ChevronLeft, Clock, Crown, Loader2, Lock } from "lucide-react";

export const Route = createFileRoute("/video/$slug")({
  head: () => ({
    meta: [
      { title: "Video — Real Pranks" },
      { name: "description", content: "Watch Real Pranks videos." },
    ],
  }),
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-3xl font-bold">Video not found</h1>
        <Link to="/" className="mt-4 inline-block text-primary hover:underline">
          ← Back to home
        </Link>
      </div>
    </div>
  ),
  component: VideoPage,
});

function VideoPage() {
  const { slug } = Route.useParams();
  const { subscriptions } = useAuth();
  const dev = useDevState();
  const isLibraryOnly = isLibraryOnlyMember(subscriptions, {
    enabled: dev.state !== "real" && dev.isSubscribed,
    tier: dev.tier,
  });
  const { video, loading } = useVideoBySlug(slug);
  const {
    canPlay,
    drop,
    reason,
    hasSubscription,
    loading: accessLoading,
  } = useVideoAccess(slug, video?.id);
  const {
    player: playerData,
    loading: playerLoading,
    error: playerError,
  } = useVideoPlayer(canPlay ? video?.id : null);

  useEffect(() => {
    if (!video) return;
    debugLog("video-page", "access state", {
      slug,
      videoId: video.id,
      creatorId: video.creator.id,
      canPlay,
      reason,
      playerLoading,
      playerError,
      showPlayer: canPlay && !!playerData?.hlsLink,
    });
  }, [slug, video, canPlay, reason, playerLoading, playerError, playerData?.hlsLink]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="mx-auto max-w-6xl px-4 py-20 text-sm text-muted-foreground">
          Loading video...
        </main>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <h1 className="text-3xl font-bold">Video not found</h1>
          <Link to="/" className="mt-4 inline-block text-primary hover:underline">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const isCurrent = drop?.status === "current";
  const isLocked = reason === "locked" || drop?.status === "locked";
  const daysUntilRelease =
    drop && isLocked
      ? Math.max(0, Math.ceil((new Date(drop.releaseAt).getTime() - Date.now()) / 86_400_000))
      : 0;
  const weeksUntil =
    drop && isLocked
      ? Math.max(1, Math.ceil((new Date(drop.releaseAt).getTime() - Date.now()) / (7 * 86_400_000)))
      : 0;

  const membershipHeadline = isCurrent
    ? "Purchase membership to view now"
    : isLocked
      ? `Purchase a membership and view this when it is released in ${weeksUntil} ${weeksUntil === 1 ? "week" : "weeks"}`
      : "Purchase Membership";

  const showPlayer = canPlay && !!playerData?.hlsLink;
  const showAccessMessage =
    hasSubscription && !canPlay && !accessLoading && reason !== "not-subscriber";

  const libraryOnlyAccessMessage = isLocked
    ? "This is next week's drop. It is not included in your Library Only plan."
    : "This video is not in your library. Library Only does not add new weekly drops.";

  const accessOverlayMessage =
    showAccessMessage && isLibraryOnly
      ? libraryOnlyAccessMessage
      : isLocked
        ? daysUntilRelease > 0
          ? `This drop releases in ${daysUntilRelease} ${daysUntilRelease === 1 ? "day" : "days"}.`
          : "This drop is not released yet."
        : "This video is not in your library.";

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <Link
          to="/library"
          className="mb-4 inline-flex items-center gap-1.5 text-base font-medium text-primary transition-colors hover:text-primary/80"
        >
          <ChevronLeft className="h-5 w-5" />
          Back to library
        </Link>

        {showPlayer ? (
          <VideoPlayerWithProgress playerData={playerData} video={video} />
        ) : (
          <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-card shadow-2xl">
            <img
              src={video.thumbnailUrl ?? ""}
              alt={video.title}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            {canPlay && playerLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              </div>
            )}
            {showAccessMessage && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 px-6 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background/95 text-foreground shadow-2xl">
                  {isLocked ? (
                    <Clock className="h-6 w-6" strokeWidth={2.5} />
                  ) : (
                    <Lock className="h-6 w-6" strokeWidth={2.5} />
                  )}
                </span>
                <p className="mt-4 max-w-sm text-sm font-semibold text-white">
                  {accessOverlayMessage}
                </p>
                {showAccessMessage && isLibraryOnly && (
                  <Button
                    asChild
                    size="sm"
                    className="mt-4 h-auto rounded-full px-4 py-2 text-xs font-semibold"
                  >
                    <Link to="/membership">Upgrade membership</Link>
                  </Button>
                )}
              </div>
            )}
            <div className="absolute right-0 bottom-0 left-0 p-6 sm:p-8">
              <h1 className="text-2xl font-extrabold leading-tight text-white sm:text-4xl">
                {video.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Link
                  to="/creator/$slug"
                  params={{ slug: video.creator.slug }}
                  className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 backdrop-blur transition-colors hover:bg-white/25"
                >
                  {video.creatorImageUrl && (
                    <img
                      src={video.creatorImageUrl}
                      alt={video.creator.name}
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  )}
                  <span className="text-sm font-medium text-white">{video.creator.name}</span>
                </Link>
                {!accessLoading && !hasSubscription && (
                  <Button
                    asChild
                    size="sm"
                    className="h-auto rounded-full px-3 py-1.5 text-xs font-semibold animate-fade-in"
                    title={membershipHeadline}
                  >
                    <Link to="/membership">
                      <Crown className="h-3.5 w-3.5" />
                      <span>Purchase Membership</span>
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {showPlayer && (
          <div className="mt-5">
            <h1 className="text-xl font-extrabold leading-tight sm:text-2xl">{video.title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Link
                to="/creator/$slug"
                params={{ slug: video.creator.slug }}
                className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1.5 ring-1 ring-border transition-colors hover:bg-accent"
              >
                {video.creatorImageUrl && (
                  <img
                    src={video.creatorImageUrl}
                    alt={video.creator.name}
                    className="h-6 w-6 rounded-full object-cover"
                  />
                )}
                <span className="text-sm font-medium">{video.creator.name}</span>
              </Link>
            </div>
          </div>
        )}

        {playerError && canPlay && <p className="mt-4 text-sm text-destructive">{playerError}</p>}

        {canPlay && hasSubscription && (
          <VideoCommentsSection
            videoId={video.id}
            creatorId={video.creator.id}
            videoStatus={video.status}
            videoFeatures={video.features ?? []}
          />
        )}
      </main>
    </div>
  );
}

function VideoPlayerWithProgress({
  playerData,
  video,
}: {
  playerData: FirestoreVideoMetaPlayer;
  video: FirebaseVideo;
}) {
  const { activity, loading: activityLoading } = useUserActivity();
  const watchTimeRef = useRef(0);
  const hasReceivedTimeRef = useRef(false);
  const lastPersistedSecondsRef = useRef<number | null>(null);
  const startTime = activity?.videoWatchTime?.[video.id] ?? 0;

  const persistWatchTime = useCallback(() => {
    if (!hasReceivedTimeRef.current) return;
    const watchTimeSeconds = watchTimeRef.current;
    if (lastPersistedSecondsRef.current === watchTimeSeconds) return;
    lastPersistedSecondsRef.current = watchTimeSeconds;
    void saveWatchTime({
      videoId: video.id,
      watchTimeSeconds,
      durationSeconds: video.duration,
    });
  }, [video.id, video.duration]);

  useEffect(() => {
    hasReceivedTimeRef.current = false;
    lastPersistedSecondsRef.current = null;
  }, [video.id]);

  useEffect(() => {
    const session = markActivity({
      onActive: persistWatchTime,
      onInactive: persistWatchTime,
    });
    return () => session.destroy();
  }, [persistWatchTime]);

  if (activityLoading) {
    return (
      <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-2xl bg-black shadow-2xl">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <JwPlayer
      playerData={playerData}
      posterUrl={video.thumbnailUrl ?? undefined}
      startTime={startTime}
      onTimeUpdate={(seconds) => {
        hasReceivedTimeRef.current = true;
        watchTimeRef.current = seconds;
      }}
    />
  );
}
